import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Eye, EyeOff } from "lucide-react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { Button } from "@/components/ui/button";
import type { NoteContentType } from "@skysend/crypto";
import hljs from "highlight.js/lib/core";
import "highlight.js/styles/github-dark.min.css";
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import python from "highlight.js/lib/languages/python";
import java from "highlight.js/lib/languages/java";
import csharp from "highlight.js/lib/languages/csharp";
import cpp from "highlight.js/lib/languages/cpp";
import go from "highlight.js/lib/languages/go";
import rust from "highlight.js/lib/languages/rust";
import php from "highlight.js/lib/languages/php";
import ruby from "highlight.js/lib/languages/ruby";
import swift from "highlight.js/lib/languages/swift";
import kotlin from "highlight.js/lib/languages/kotlin";
import sql from "highlight.js/lib/languages/sql";
import bash from "highlight.js/lib/languages/bash";
import shell from "highlight.js/lib/languages/shell";
import json from "highlight.js/lib/languages/json";
import xml from "highlight.js/lib/languages/xml";
import css from "highlight.js/lib/languages/css";
import yaml from "highlight.js/lib/languages/yaml";
import markdown from "highlight.js/lib/languages/markdown";
import docker from "highlight.js/lib/languages/dockerfile";
import ini from "highlight.js/lib/languages/ini";
import plaintext from "highlight.js/lib/languages/plaintext";
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("python", python);
hljs.registerLanguage("java", java);
hljs.registerLanguage("csharp", csharp);
hljs.registerLanguage("cpp", cpp);
hljs.registerLanguage("go", go);
hljs.registerLanguage("rust", rust);
hljs.registerLanguage("php", php);
hljs.registerLanguage("ruby", ruby);
hljs.registerLanguage("swift", swift);
hljs.registerLanguage("kotlin", kotlin);
hljs.registerLanguage("sql", sql);
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("shell", shell);
hljs.registerLanguage("json", json);
hljs.registerLanguage("xml", xml);
hljs.registerLanguage("css", css);
hljs.registerLanguage("yaml", yaml);
hljs.registerLanguage("markdown", markdown);
hljs.registerLanguage("dockerfile", docker);
hljs.registerLanguage("ini", ini);
hljs.registerLanguage("plaintext", plaintext);
interface NoteContentProps {
  content: string;
  contentType: NoteContentType;
}
export function NoteContent({ content, contentType }: NoteContentProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copiedPublic, setCopiedPublic] = useState(false);
  const [copiedPrivate, setCopiedPrivate] = useState(false);
  const [copiedPassphrase, setCopiedPassphrase] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState<Set<number>>(new Set());
  const [copiedPasswords, setCopiedPasswords] = useState<Set<number>>(new Set());
  const [copiedUsernames, setCopiedUsernames] = useState<Set<number>>(new Set());
  const [copiedLabels, setCopiedLabels] = useState<Set<number>>(new Set());
  const [copiedIps, setCopiedIps] = useState<Set<number>>(new Set());
  const [copiedObjectStorageFields, setCopiedObjectStorageFields] = useState<Set<string>>(new Set());
  const [revealedObjectStorageSecret, setRevealedObjectStorageSecret] = useState(false);
  const highlightedCode = useMemo(() => {
    if (contentType !== "code") return "";
    const raw = hljs.highlightAuto(content).value;
    return DOMPurify.sanitize(raw, { ALLOWED_TAGS: ["span"], ALLOWED_ATTR: ["class"] });
  }, [content, contentType]);
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyObjectStorageField = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }

    setCopiedObjectStorageFields((prev) => new Set(prev).add(key));

    setTimeout(() => {
      setCopiedObjectStorageFields((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 2000);
  };

  const renderObjectStorageField = (
    label: string,
    value: string,
    keyName: string,
    secret = false,
  ) => {
    if (!value) return null;

    const visibleValue =
      secret && !revealedObjectStorageSecret
        ? "•".repeat(Math.min(value.length, 48))
        : value;

    return (
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm break-all">
            {visibleValue}
          </div>

          {secret && (
            <Button
              variant="outline"
              size="icon"
              className="shrink-0"
              onClick={() => setRevealedObjectStorageSecret((v) => !v)}
              title={revealedObjectStorageSecret ? t("noteView.hide") : t("noteView.reveal")}
            >
              {revealedObjectStorageSecret ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}

          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={() => copyObjectStorageField(keyName, value)}
            title={
              copiedObjectStorageFields.has(keyName)
                ? t("common.copied")
                : t("common.copy")
            }
          >
            {copiedObjectStorageFields.has(keyName) ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    );
  };

  if (contentType === "password") {
    try {
      const parsed = JSON.parse(content) as Record<string, unknown>;

      if (
        parsed &&
        typeof parsed === "object" &&
        parsed.kind === "object-storage-auth"
      ) {
        const tenantAccountName =
          typeof parsed.tenantAccountName === "string"
            ? parsed.tenantAccountName
            : "";
        const endpointUrl =
          typeof parsed.endpointUrl === "string" ? parsed.endpointUrl : "";
        const accessKeyId =
          typeof parsed.accessKeyId === "string" ? parsed.accessKeyId : "";
        const secretAccessKey =
          typeof parsed.secretAccessKey === "string"
            ? parsed.secretAccessKey
            : "";
        const region = typeof parsed.region === "string" ? parsed.region : "";

        const capacityLimit =
          parsed.capacityLimit &&
          typeof parsed.capacityLimit === "object"
            ? (parsed.capacityLimit as Record<string, unknown>)
            : null;

        const capacityText =
          capacityLimit &&
          typeof capacityLimit.value === "string" &&
          capacityLimit.value.length > 0
            ? `${capacityLimit.value} ${String(capacityLimit.unit ?? "")}`.trim()
            : "";

        const expirationDate =
          parsed.expirationDate &&
          typeof parsed.expirationDate === "object"
            ? (parsed.expirationDate as Record<string, unknown>)
            : null;

        const date = String(expirationDate?.date ?? "");
        const time = String(expirationDate?.time ?? "");
        const period = String(expirationDate?.period ?? "");

        const oldDay = String(expirationDate?.day ?? "");
        const oldMonth = String(expirationDate?.month ?? "");
        const oldYear = String(expirationDate?.year ?? "");
        const oldHour = String(expirationDate?.hour ?? "");
        const oldMinute = String(expirationDate?.minute ?? "");

        const oldExpirationText =
          oldDay || oldMonth || oldYear || oldHour || oldMinute
            ? `${oldDay.padStart(2, "0")}/${oldMonth.padStart(2, "0")}/${oldYear} ${oldHour.padStart(2, "0")}:${oldMinute.padStart(2, "0")}`
            : "";

        const expirationText =
          date || time || period
            ? `${date} ${time} ${period}`.trim()
            : oldExpirationText;

        const buckets = Array.isArray(parsed.buckets)
          ? parsed.buckets
              .filter((bucket) => typeof bucket === "object" && bucket !== null)
              .map((bucket) => {
                const obj = bucket as Record<string, unknown>;
                return {
                  name: typeof obj.name === "string" ? obj.name : "",
                  objectLock: Boolean(obj.objectLock),
                };
              })
          : [];

        return (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">
                  {t("objectStorage.credentials")}
                </span>
              </div>

              <div className="space-y-3">
                {renderObjectStorageField(
                  t("objectStorage.tenantAccountName"),
                  tenantAccountName,
                  "tenantAccountName",
                )}
                {renderObjectStorageField(
                  t("objectStorage.endpointUrl"),
                  endpointUrl,
                  "endpointUrl",
                )}
                {renderObjectStorageField(
                  t("objectStorage.accessKeyId"),
                  accessKeyId,
                  "accessKeyId",
                )}
                {renderObjectStorageField(
                  t("objectStorage.secretAccessKey"),
                  secretAccessKey,
                  "secretAccessKey",
                  true,
                )}
                {renderObjectStorageField(
                  t("objectStorage.region"),
                  region,
                  "region",
                )}
                {renderObjectStorageField(
                  t("objectStorage.capacity"),
                  capacityText,
                  "capacity",
                )}
                {renderObjectStorageField(
                  t("objectStorage.expirationDate"),
                  expirationText,
                  "expirationDate",
                )}
              </div>
            </div>

            {buckets.length > 0 && (
              <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                <span className="text-sm font-semibold">
                  {t("objectStorage.buckets")}
                </span>

                {buckets.map((bucket, index) => (
                  <div
                    key={index}
                    className="space-y-2 rounded-md border bg-background/50 p-3"
                  >
                    {renderObjectStorageField(
                      t("objectStorage.bucketName", { number: index + 1 }),
                      bucket.name,
                      `bucket-${index}`,
                    )}

                    {bucket.objectLock && (
                      <div className="rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">
                        {t("objectStorage.objectLock")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    } catch {
      // Not Object Storage JSON. Continue with the normal password renderer.
    }
  }

  if (contentType === "password") {
    let entries: { label: string; ip: string; username: string; value: string }[];

    try {
      const parsed = JSON.parse(content) as unknown[];

      if (
        Array.isArray(parsed) &&
        parsed.every((e) => typeof e === "object" && e !== null && "value" in e)
      ) {
        entries = parsed.map((e) => {
          const obj = e as Record<string, unknown>;

          return {
            label: typeof obj.label === "string" ? obj.label : "",
            ip: typeof obj.ip === "string" ? obj.ip : "",
            username: typeof obj.username === "string" ? obj.username : "",
            value: String(obj.value),
          };
        });
      } else {
        throw new Error("Not password JSON");
      }
    } catch {
      entries = content
        .split("\n\n")
        .filter((p) => p.length > 0)
        .map((p) => ({
          label: "",
          ip: "",
          username: "",
          value: p,
        }));
    }

    const togglePasswordReveal = (index: number) => {
      setRevealedPasswords((prev) => {
        const next = new Set(prev);

        if (next.has(index)) next.delete(index);
        else next.add(index);

        return next;
      });
    };

    const copyEntryText = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
    };

    const copyLabel = async (text: string, index: number) => {
      await copyEntryText(text);

      setCopiedLabels((prev) => new Set(prev).add(index));

      setTimeout(() => {
        setCopiedLabels((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, 2000);
    };

    const copyIp = async (text: string, index: number) => {
      await copyEntryText(text);

      setCopiedIps((prev) => new Set(prev).add(index));

      setTimeout(() => {
        setCopiedIps((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, 2000);
    };

    const copyUsername = async (text: string, index: number) => {
      await copyEntryText(text);

      setCopiedUsernames((prev) => new Set(prev).add(index));

      setTimeout(() => {
        setCopiedUsernames((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, 2000);
    };

    const copyPassword = async (text: string, index: number) => {
      await copyEntryText(text);

      setCopiedPasswords((prev) => new Set(prev).add(index));

      setTimeout(() => {
        setCopiedPasswords((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, 2000);
    };

    return (
      <div className="space-y-4">
        {entries.map((entry, index) => (
          <div key={index} className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
            {entry.label ? (
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">{t("password.nameOrUrl")}</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm break-all">
                    {entry.label}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => copyLabel(entry.label, index)}
                    title={copiedLabels.has(index) ? t("common.copied") : t("common.copy")}
                  >
                    {copiedLabels.has(index) ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {t("password.passwordNumber", { number: index + 1 })}
              </span>
            )}

            {entry.ip && (
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">{t("password.ipAddress")}</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm break-all">
                    {entry.ip}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => copyIp(entry.ip, index)}
                    title={copiedIps.has(index) ? t("common.copied") : t("common.copy")}
                  >
                    {copiedIps.has(index) ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {entry.username && (
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">{t("password.username")}</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm break-all">
                    {entry.username}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => copyUsername(entry.username, index)}
                    title={copiedUsernames.has(index) ? t("common.copied") : t("common.copy")}
                  >
                    {copiedUsernames.has(index) ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">{t("password.password")}</span>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm break-all">
                  {revealedPasswords.has(index)
                    ? entry.value
                    : "•".repeat(Math.min(entry.value.length, 40))}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => togglePasswordReveal(index)}
                  title={revealedPasswords.has(index) ? t("noteView.hide") : t("noteView.reveal")}
                >
                  {revealedPasswords.has(index) ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => copyPassword(entry.value, index)}
                  title={copiedPasswords.has(index) ? t("common.copied") : t("common.copy")}
                >
                  {copiedPasswords.has(index) ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (contentType === "sshkey") {
    const passphraseMatch = content.match(/^Passphrase: (.+)$/m);
    const passphrase = passphraseMatch?.[1] ?? null;
    const contentWithoutPassphrase = passphrase ? content.replace(passphraseMatch![0], "").trim() : content;
    const privateKeyMatch = contentWithoutPassphrase.match(/(-----BEGIN[^\n]*PRIVATE KEY-----[\s\S]*?-----END[^\n]*PRIVATE KEY-----)/);
    const privateKey = privateKeyMatch?.[1]?.trim() ?? null;
    const publicKey = privateKey ? contentWithoutPassphrase.replace(privateKey, "").trim() : contentWithoutPassphrase.trim();
    const copyText = async (text: string, setter: (v: boolean) => void) => {
      try { await navigator.clipboard.writeText(text); setter(true); setTimeout(() => setter(false), 2000); } catch {}
    };
    return (
      <div className="space-y-4">
        {publicKey && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("sshKey.publicKey")}</span>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copyText(publicKey, setCopiedPublic)}>
                {copiedPublic ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                {copiedPublic ? t("common.copied") : t("common.copy")}
              </Button>
            </div>
            <pre className="overflow-x-auto rounded-lg border bg-muted/50 p-3 font-mono text-xs break-all whitespace-pre-wrap scrollbar-thin">{publicKey}</pre>
          </div>
        )}
        {privateKey && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("sshKey.privateKey")}</span>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copyText(privateKey, setCopiedPrivate)}>
                {copiedPrivate ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                {copiedPrivate ? t("common.copied") : t("common.copy")}
              </Button>
            </div>
            <pre className="max-h-40 overflow-auto rounded-lg border bg-muted/50 p-3 font-mono text-xs scrollbar-thin">{privateKey}</pre>
          </div>
        )}
        {passphrase && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("sshKey.passphrase")}</span>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copyText(passphrase, setCopiedPassphrase)}>
                {copiedPassphrase ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                {copiedPassphrase ? t("common.copied") : t("common.copy")}
              </Button>
            </div>
            <pre className="overflow-x-auto rounded-lg border bg-muted/50 p-3 font-mono text-xs break-all whitespace-pre-wrap scrollbar-thin">{passphrase}</pre>
          </div>
        )}
      </div>
    );
  }
  if (contentType === "code") {
    const lines = content.split("\n");
    const lineNumberWidth = String(lines.length).length;
    return (
      <div className="space-y-3">
        <div className="relative overflow-hidden rounded-lg border bg-[#0d1117]">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full border-collapse text-sm font-mono">
              <tbody>
                {highlightedCode.split("\n").map((line, i) => (
                  <tr key={i} className="hover:bg-white/5">
                    <td className="select-none border-r border-white/10 px-3 py-0.5 text-right text-muted-foreground/50" style={{ minWidth: `${lineNumberWidth + 2}ch` }}>{i + 1}</td>
                    <td className="px-4 py-0.5"><span dangerouslySetInnerHTML={{ __html: line || "\n" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={copyToClipboard}>
          {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
          {copied ? t("common.copied") : t("common.copy")}
        </Button>
      </div>
    );
  }
  if (contentType === "markdown") {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border bg-muted/50 p-4 prose prose-sm dark:prose-invert max-w-none overflow-auto">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{content}</ReactMarkdown>
        </div>
        <Button variant="outline" size="sm" onClick={copyToClipboard}>
          {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
          {copied ? t("common.copied") : t("common.copy")}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="whitespace-pre-wrap rounded-lg border bg-muted/50 p-4 text-sm wrap-break-word">{content}</div>
      <Button variant="outline" size="sm" onClick={copyToClipboard}>
        {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
        {copied ? t("common.copied") : t("common.copy")}
      </Button>
    </div>
  );
}
